'use client';
import React from 'react';
import classnames from 'classnames';
import styles from './Peer.module.scss';
import { formatRoundFixed } from '@benzinga/utils/numberUtils';
import { AFTER_MARKET, PRE_MARKET, REGULAR } from '@benzinga/quotes-manager/constants';
import type { PeerQuote } from '@benzinga/quotes-manager';
import { Up as UpIcon } from '@benzinga/icons';

const PeerChart = React.lazy(() =>
  import('./PeerChart').then(module => {
    return { default: module.PeerChart };
  }),
);

interface PeerProps {
  peerData: PeerQuote;
  currentSymbol: string;
  type: string;
}

const Peer: React.FC<PeerProps> = ({ peerData, type }) => {
  const symbol = peerData?.symbol;
  // const session = React.useContext(SessionContext);

  // const chartManager = React.useMemo(() => session.getManager(ChartManager), [session]);
  // onst [chartData, setChartData] = React.useState<ChartDataResponse>(chart[currentSymbol]);

  // React.useEffect(() => {
  //   if (!symbol) return;

  //   async function getChartData() {
  //     const res = await chartManager.getChart(symbol, '1d', '2m');
  //     setChartData(res.ok);
  //   }

  //   getChartData();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [symbol]);

  let chartColors: [string, string, string] = ['#1060FF4D', '#1060FF00', '#1060FF'];

  // quote has loaded
  if (peerData && peerData.lastTradePrice) {
    let color = 'text-blue-600';
    let price = peerData.lastTradePrice;
    let changePercent = peerData.changePercent;

    if (type === PRE_MARKET) {
      color = 'text-black';
    } else if (peerData.change !== undefined && peerData.change < 0) {
      color = 'text-red-600';
      chartColors = ['#FF40504D', '#FF405000', '#FF4050'];
    } else if (peerData.change !== undefined && peerData.change > 0) {
      color = 'text-green-600';
      chartColors = ['#0F993D4D', '#0F993D00', '#0F993D'];
    }

    if ((type === PRE_MARKET || type === AFTER_MARKET) && peerData.ethPrice !== undefined) {
      price = peerData.ethPrice;
      const change = price - peerData.lastTradePrice || peerData.change || 0;
      changePercent = (change / peerData.lastTradePrice) * 100 || peerData.changePercent;

      if (change > 0) {
        color = 'text-green-600';
        chartColors = ['#0F993D4D', '#0F993D00', '#0F993D'];
      } else if (change < 0) {
        color = 'text-red-600';
        chartColors = ['#FF40504D', '#FF405000', '#FF4050'];
      }
    }

    let data: number[] = [];

    // chart has loaded
    if (Array.isArray(peerData?.chartCandles)) {
      data = [...peerData.chartCandles]; // candles are already close prices
      const min = Math.min(...data);
      data = data.map(point => point - min);
    }

    if (type === REGULAR) {
      while (data.length < 19) {
        data.push(0);
      }
    }

    const sumOfChartData = data.reduce((dataPointA, dataPointB) => dataPointA + dataPointB, 0);

    return (
      <div className={classnames(styles.peerWrapper, 'rounded-xl mb-2 block')}>
        <div className="w-full h-full block">
          <a className="w-full h-full" href={`/quote/${symbol}`}>
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                <div className="font-bold symbol">{peerData?.companyStandardName}</div>
                <div className="text-xs name">
                  {peerData?.bzExchange}:{peerData?.symbol}
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="font-bold text-black">${formatRoundFixed(price, 2)}</div>
                <div className="flex">
                  <span className="flex items-center justify-center h-[15px] w-[21px]">
                    {changePercent !== 0 && color === 'text-red-600' && (
                      <UpIcon className={classnames(styles.arrow, styles.arrowDown)} />
                    )}
                    {changePercent !== 0 && color === 'text-green-600' && (
                      <UpIcon className={classnames(styles.arrow, styles.arrowUp)} />
                    )}
                  </span>
                  <span className={classnames(color)}>
                    {changePercent !== undefined && changePercent !== 0 ? formatRoundFixed(changePercent, 2) : '-'}%
                  </span>
                </div>
              </div>
            </div>
            <div>
              {/* <Sparkline className="block" data={data} height={23} width={250} /> */}
              {sumOfChartData === 0 ? (
                <div className="text-black text-sm">No data available</div>
              ) : (
                <React.Suspense fallback={<div className="h-[50px]" />}>
                  <PeerChart colors={chartColors} seriesData={data} />
                </React.Suspense>
              )}
            </div>
          </a>
        </div>
      </div>
    );
  }

  // Return an empty row with just the symbol.
  return (
    <div className="border-b">
      <div>{symbol}</div>
      <div />
      <div />
      <div />
      <div />
      <div />
    </div>
  );
};

export default Peer;
